<script setup lang="ts">
    import { useRouter, useRoute } from 'vue-router';
    import type { IssueReportCounts } from '../../interfaces';
    import { formatNumericText } from '@shared/utils/formatNumericText';

    function fmtClick(v: number) {
        return formatNumericText(v, { suffix: '%', precision: v > 0 && v < 1 ? 1 : 0 });
    }

    const props = defineProps<{
        overview: IssueReportCounts | null;
        categoryAverages: IssueReportCounts | null;
        isAnonymousTracking: boolean;
    }>();

    const router = useRouter();
    const route = useRoute();

    const anonymousAllowed = new Set(['Sent', 'Bounced', 'Unsubscribed']);

    function isClickable(contactType: string) {
        return !props.isAnonymousTracking || anonymousAllowed.has(contactType);
    }

    function navigateTo(contactType: string) {
        if (!isClickable(contactType)) return;
        router.push({
            name: 'IssueStatisticsContacts',
            params: { issueId: route.params.issueId, contactType },
            query: { ...route.query },
        });
    }
</script>

<template>
    <div class="stat-highlights panel panel-default print-block">
        <div class="row no-gutter d-flex flex-wrap">
            <div class="flex-col col-md-3 col-sm-6 col-xs-12 col-print-3">
                <div class="stat-highlights-body ug-bars pointer" @click="navigateTo('Sent')">
                    <p class="bar-label">{{ $Resources.IssuesSentLabel }}</p>
                    <p class="value">{{ formatNumericText(overview?.SentCount || 0, { largeNumbers: true }) }}</p>
                    <div class="bar">
                        <span class="bar-value bg-sent bar-transition" :style="{ width: overview?.SentPercentage + '%' }"></span>
                    </div>
                    <p v-if="categoryAverages">
                        <span class="glyphicon glyphicon-tag" aria-hidden="true"></span>
                        {{ $Resources.AverageLabel }}: {{ formatNumericText(categoryAverages?.SentCount || 0, { largeNumbers: true }) }}
                    </p>
                </div>
            </div>

            <div class="flex-col col-md-3 col-sm-6 col-xs-12 col-print-3">
                <div :class="['stat-highlights-body ug-bars', { pointer: isClickable('Received') }]" @click="navigateTo('Received')">
                    <p class="bar-label">{{ $Resources.IssuesReceivedPercentageLabel }}</p>
                    <p class="value">{{ formatNumericText(overview?.ReceivedPercentage || 0, { suffix: '%' }) }}</p>
                    <div class="bar">
                        <span class="bar-value bg-received bar-transition" :style="{ width: overview?.ReceivedPercentage + '%' }"></span>
                    </div>
                    <p v-if="categoryAverages">
                        <span class="glyphicon glyphicon-tag" aria-hidden="true"></span>
                        {{ $Resources.AverageLabel }}: {{ formatNumericText(categoryAverages?.ReceivedPercentage || 0, { suffix: '%' }) }}
                    </p>
                </div>
            </div>

            <div class="flex-col col-md-3 col-sm-6 col-xs-12 col-print-3">
                <div :class="['stat-highlights-body ug-bars', { pointer: isClickable('Opened') }]" @click="navigateTo('Opened')">
                    <p class="bar-label">{{ $Resources.IssuesOpenedPercentageLabel }}</p>
                    <p class="value">{{ formatNumericText(overview?.OpenPercentage || 0, { suffix: '%' }) }}</p>
                    <div class="bar">
                        <span class="bar-value bg-opened bar-transition" :style="{ width: overview?.OpenPercentage + '%' }"></span>
                    </div>
                    <p v-if="categoryAverages">
                        <span class="glyphicon glyphicon-tag" aria-hidden="true"></span>
                        {{ $Resources.AverageLabel }}: {{ formatNumericText(categoryAverages?.OpenPercentage || 0, { suffix: '%' }) }}
                    </p>
                </div>
            </div>

            <div class="flex-col col-md-3 col-sm-6 col-xs-12 col-print-3">
                <div :class="['stat-highlights-body ug-bars', { pointer: isClickable('Clicked') }]" @click="navigateTo('Clicked')">
                    <p class="bar-label">{{ $Resources.IssuesClickedPercentageLabel }}</p>
                    <p class="value">{{ fmtClick(overview?.ClickPercentage || 0) }}</p>
                    <div class="bar">
                        <span class="bar-value bg-clicked bar-transition" :style="{ width: overview?.ClickPercentage + '%' }"></span>
                    </div>
                    <p v-if="categoryAverages">
                        <span class="glyphicon glyphicon-tag" aria-hidden="true"></span>
                        {{ $Resources.AverageLabel }}: {{ fmtClick(categoryAverages?.ClickPercentage || 0) }}
                    </p>
                </div>
            </div>
        </div>
    </div>
</template>
